'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ContactMethod {
  id: string;
  icon: string;
  label: string;
  value: string;
  action: string;
}

interface ContactCTASystemProps {
  variant?: 'inline' | 'sticky' | 'modal';
  showCalculatorLink?: boolean;
}

const contactOptions = [
{
  id: 'email',
  icon: 'EnvelopeIcon',
  label: 'Send E-post',
  value: 'post@gullklar.no',
  action: 'mailto:post@gullklar.no'
}];


const ContactCTASystem = ({
  variant = 'inline',
  showCalculatorLink = true
}: ContactCTASystemProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [scrollDepth, setScrollDepth] = useState<number>(0);

  useEffect(() => {
    if (variant === 'sticky') {
      const handleScroll = () => {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY;
        const depth = scrollTop / (documentHeight - windowHeight) * 100;

        setScrollDepth(depth);
        setIsVisible(scrollTop > 300);
      };

      window.addEventListener('scroll', handleScroll);
      handleScroll();

      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [variant]);

  const handleContactClick = (action: string) => {
    if (action === '#') {
      alert('Chat-funksjonalitet kommer snart!');
      return;
    }
    window.location.href = action;
  };

  const scrollToCalculator = () => {
    const element = document.getElementById('priskalkulator');
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  if (variant === 'sticky') {
    return (
      <>
        {isVisible &&
        <div className="fixed bottom-0 left-0 right-0 z-[90] bg-card shadow-md lg:hidden">
            <div className="flex items-center justify-between px-5 py-3">
              {scrollDepth < 50 ?
            <>
                  <button
                onClick={() => setShowCalculator(true)}
                className="flex items-center justify-center rounded-lg bg-accent px-4 py-3 font-cta text-sm font-semibold text-accent-foreground shadow-cta transition-all duration-250 hover:bg-accent/90">

                    <Icon name="CalculatorIcon" size={18} variant="solid" />
                    <span className="ml-2">Beregn verdi</span>
                  </button>
                </> :

            <>
                  <a
                href="mailto:kontakt@gullklar.no"
                className="flex flex-1 items-center justify-center rounded-lg border-2 border-primary bg-background px-4 py-3 font-cta text-sm font-semibold text-primary transition-colors duration-250 hover:bg-primary/10">

                    <Icon name="EnvelopeIcon" size={18} variant="solid" />
                  </a>
                  <button
                onClick={scrollToCalculator}
                className="ml-3 flex items-center justify-center rounded-lg border-2 border-primary bg-background px-4 py-3 font-cta text-sm font-semibold text-primary transition-colors duration-250 hover:bg-primary/10">

                    <Icon name="CalculatorIcon" size={18} variant="outline" />
                  </button>
                </>
            }
            </div>
          </div>
        }
      </>);

  }

  return (
    <div id="kontakt" className="bg-card py-12 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mb-10 text-center">
          <h2 className="font-headline text-3xl font-bold text-foreground lg:text-4xl">
            Kontakt oss i dag
          </h2>
          <p className="mt-3 font-body text-base text-muted-foreground lg:text-lg">
            Velg din foretrukne kontaktmetode - vi avtaler hjemmebesøk som passer deg
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {contactOptions.map((option) =>
          <button
            key={option.id}
            onClick={() => handleContactClick(option.action)}
            className="group flex flex-col items-center space-y-4 rounded-lg border border-border bg-background p-6 text-center transition-all duration-250 hover:border-primary hover:shadow-cta lg:p-8">

              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 transition-colors duration-250 group-hover:bg-primary">
                <Icon
                name={option.icon as any}
                size={32}
                variant="solid"
                className="text-primary transition-colors duration-250 group-hover:text-primary-foreground" />

              </div>
              <div>
                <h3 className="font-cta text-lg font-semibold text-foreground">
                  {option.label}
                </h3>
                <p className="mt-2 font-body text-sm text-muted-foreground">
                  {option.value}
                </p>
              </div>
            </button>
          )}
        </div>

        {showCalculatorLink &&
        <div className="mt-10 text-center">
            <button
            onClick={scrollToCalculator}
            className="inline-flex items-center space-x-2 rounded-lg bg-accent px-8 py-4 font-cta text-base font-semibold text-accent-foreground shadow-cta transition-all duration-250 hover:bg-accent/90 lg:text-lg">

              <Icon name="CalculatorIcon" size={20} variant="outline" />
              <span>Bestill hjemmebesøk</span>
            </button>
          </div>
        }

        <div className="mt-12 rounded-lg bg-muted p-6 lg:p-8">
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <h3 className="mb-3 font-headline text-xl font-semibold text-foreground">
                Åpningstider
              </h3>
              <div className="space-y-2 font-body text-sm text-muted-foreground">
                <div className="flex justify-between">
                  <span>Mandag - Fredag:</span>
                  <span className="font-medium text-foreground">09:00 - 17:00</span>
                </div>
                <div className="flex justify-between">
                  <span>Lørdag:</span>
                  <span className="font-medium text-foreground">10:00 - 14:00</span>
                </div>
                <div className="flex justify-between">
                  <span>Søndag:</span>
                  <span className="font-medium text-foreground">Stengt</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="mb-3 font-headline text-sm font-semibold text-muted-foreground">
                Kontoraddresse
              </h3>
              <div className="space-y-2 font-body text-sm text-muted-foreground">
                <p>Gullklar</p>
                <p>Kristiansand
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-4 lg:gap-6">
          <div className="flex items-center space-x-2">
            <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-success" />
            <span className="font-body text-sm font-medium text-foreground">Gratis hjemmebesøk</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="ClockIcon" size={20} variant="solid" className="text-success" />
            <span className="font-body text-sm font-medium text-foreground">Oppgjør på stedet</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="CheckBadgeIcon" size={20} variant="solid" className="text-success" />
            <span className="font-body text-sm font-medium text-foreground">Ingen forpliktelser</span>
          </div>
        </div>
      </div>
    </div>);
};

export default ContactCTASystem;